import { useState, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";

const PLAN_COLORS: Record<string, string> = {
  free: "#ffffff",
  fan: "#ff6eb4",
  superfan: "#4fc3f7",
};

const LEVEL_TITLES = [
  { min: 0, label: "뉴비 팬" },
  { min: 5000, label: "열정 팬" },
  { min: 15000, label: "코어 팬" },
  { min: 30000, label: "리워드 헌터" },
  { min: 50000, label: "전설의 팬" },
];

function getLevelInfo(xp: number) {
  let current = LEVEL_TITLES[0];
  let nextXP = 5000;
  for (let i = LEVEL_TITLES.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_TITLES[i].min) {
      current = LEVEL_TITLES[i];
      nextXP = i < LEVEL_TITLES.length - 1 ? LEVEL_TITLES[i + 1].min : xp;
      break;
    }
  }
  const progress = Math.min(100, Math.round((xp / nextXP) * 100));
  return { label: current.label, nextXP, progress };
}

interface Props {
  onEditProfile: () => void;
}

export default function ProfileHeader({ onEditProfile }: Props) {
  const { user } = useAuth();
  const [bannerHover, setBannerHover] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  if (!user) return null;

  const planColor = PLAN_COLORS[user.plan] || "#ff6eb4";
  const { label: levelLabel, nextXP, progress } = getLevelInfo(user.totalXP);

  return (
    <div className="rounded-2xl border border-white/10 overflow-hidden bg-white/[0.03]">
      {/* Banner */}
      <div
        className="relative h-32 cursor-pointer"
        style={{
          background: `linear-gradient(135deg, ${planColor}25, #4fc3f7 18, #0a0a0f 70%)`,
        }}
        onMouseEnter={() => setBannerHover(true)}
        onMouseLeave={() => setBannerHover(false)}
        onClick={() => fileRef.current?.click()}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/e164a754b857422ee5588c1895ad15d8.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.5,
          }}
        />
        {bannerHover && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <i className="ri-image-edit-line" />
              배너 변경
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" />
      </div>

      {/* Profile info row */}
      <div className="px-6 pb-6">
        <div className="flex items-end justify-between -mt-10 mb-4">
          {/* Avatar */}
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl overflow-hidden border-4 border-[#0a0a0f]">
              <img
                src={user.avatar}
                alt={user.nickname}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div
              className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full border-2 border-[#0a0a0f]"
              style={{ background: planColor }}
            >
              <i className="ri-vip-crown-fill text-white text-[10px]" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pb-1">
            <button
              onClick={onEditProfile}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-white/15 text-white/70 text-sm font-medium hover:border-white/30 hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line text-sm" />
              프로필 편집
            </button>
          </div>
        </div>

        {/* Name & info */}
        <div className="mb-4">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="text-xl font-black text-white">{user.nickname}</h2>
            <span
              className="px-2.5 py-0.5 rounded-full text-xs font-bold"
              style={{ background: `${planColor}20`, color: planColor, border: `1px solid ${planColor}40` }}
            >
              {user.planLabel}
            </span>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/8 text-white/50 border border-white/10">
              {levelLabel}
            </span>
          </div>
          <p className="text-white/40 text-sm mt-1">{user.email}</p>
          {user.bio && (
            <p className="text-white/60 text-sm mt-2">{user.bio}</p>
          )}
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="flex items-center gap-1.5 text-white/40 text-xs">
              <i className="ri-heart-line text-[#ff6eb4]" />
              최애: <span className="text-white/60 font-semibold">{user.favoriteGroup}</span>
            </span>
            <span className="flex items-center gap-1.5 text-white/40 text-xs">
              <i className="ri-calendar-line" />
              가입: <span className="text-white/60">{user.joinedAt.slice(0, 7)}</span>
            </span>
          </div>
        </div>

        {/* XP Bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/50 text-xs font-semibold">총 XP</span>
            <span className="text-white/70 text-xs font-bold">
              <span style={{ color: planColor }}>{user.totalXP.toLocaleString()}</span>
              <span className="text-white/30 mx-1">/</span>
              {nextXP.toLocaleString()} XP
            </span>
          </div>
          <div className="h-2 rounded-full bg-white/8 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${planColor}, #4fc3f7)` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-white/25 text-[10px]">다음 레벨까지 {(nextXP - user.totalXP).toLocaleString()} XP 남음</span>
            <span className="text-white/25 text-[10px]">{progress}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
